import {
  classifyError,
  FetchError,
  isSafeServerDetail,
  toUserMessage,
  UserMessageError,
  type AddNewsArticle,
  NewsArticle,
  useAuth
} from 'prove-common'

type NewsClient = ReturnType<ReturnType<typeof useAuth>['authFetch']>

export function newsApiError(
  error: unknown,
  fallback = 'Forespørselen kunne ikke fullføres. Prøv igjen.'
): Error {
  if (error instanceof Error) return error
  if (typeof error === 'string' && error) return new Error(error)
  if (error && typeof error === 'object') {
    for (const key of ['detail', 'message', 'title']) {
      if (
        key in error &&
        typeof error[key as keyof typeof error] === 'string'
      ) {
        return new Error(String(error[key as keyof typeof error]))
      }
    }
  }
  return new Error(fallback)
}

/**
 * User-facing Norwegian text for a news API failure. Route every classified
 * or status-bearing error (auth, not-found, conflict, validation, server, ...)
 * through `toUserMessage` so raw status details such as the backend's English
 * 404 text never leak into the Norwegian UI. Only unclassified, status-less
 * values (plain curated business errors such as "Artikkelen finnes ikke.") may
 * use the raw message verbatim. The raw error is logged by the callers, so no
 * diagnostic detail is lost.
 */
export function newsUserMessage(error: unknown): string {
  const classified = classifyError(error)
  if (classified.kind !== 'unknown' || classified.status !== undefined) {
    return toUserMessage(error)
  }
  const normalized = newsApiError(error)
  return isSafeServerDetail(normalized.message)
    ? normalized.message
    : toUserMessage(error)
}

export async function saveNewsArticle({
  client,
  body,
  articleId,
  savedArticle,
  image,
  onContentSaved
}: {
  client: NewsClient
  body: AddNewsArticle
  articleId?: number
  savedArticle?: NewsArticle
  image?: File
  onContentSaved: (article: NewsArticle) => void
}): Promise<NewsArticle> {
  let saved = savedArticle
  if (!saved) {
    const result =
      articleId === undefined
        ? await client.POST('/news', { body })
        : await client.PUT('/news/{articleId}', {
            params: { path: { articleId } },
            body
          })
    if (!result.response.ok || result.error) {
      const message = newsApiError(result.error).message
      throw new FetchError(message, result.response.status, message)
    }
    if (!result.data?.id)
      throw new UserMessageError(
        'Serveren returnerte ikke en artikkel-ID. Kontroller nyhetslisten før du prøver igjen.'
      )
    saved = result.data
    // Checkpoint before uploading: a rejected image must never repeat POST /news.
    onContentSaved(saved)
  }
  if (image) {
    const form = new FormData()
    form.append('file', image)
    const result = await client.POST('/news/{articleId}/image', {
      params: { path: { articleId: saved.id } },
      body: { file: image.name },
      bodySerializer: () => form,
      parseAs: 'text'
    })
    if (!result.response.ok || result.error) {
      const message = newsApiError(result.error).message
      throw new FetchError(message, result.response.status, message)
    }
  }
  return saved
}
